import * as React from 'react';

import { css } from '@emotion/react';
import { Box } from '@opendoor/bricks-next';
import { Flex } from '@opendoor/bricks/core';

import FlowElement from 'src/components/fields/FlowElement';
import PhoneInput from 'src/components/fields/PhoneInput';
import BuilderPartnersSelectField from 'src/components/novo/fields/BuilderPartnerSelectField';
import CheckboxGroup from 'src/components/novo/fields/CheckboxGroup';
import CurrencyField from 'src/components/novo/fields/CurrencyField';
import EmailInput from 'src/components/novo/fields/EmailInput';
import ImageSelectInput from 'src/components/novo/fields/ImageSelectInput';
import MlsNamesSelectField from 'src/components/novo/fields/MlsNamesSelectField';
import NumberInput from 'src/components/novo/fields/NumberInput';
import NumberRangeInputField from 'src/components/novo/fields/NumberRangeInput';
import NumberRangeSelectInput from 'src/components/novo/fields/NumberRangeSelectInput';
import RadioGroup from 'src/components/novo/fields/RadioGroup';
import SelectInput from 'src/components/novo/fields/SelectInput';
import TextArea from 'src/components/novo/fields/TextArea';
import SingleLineInput from 'src/components/novo/fields/TextInput';
import { FlowQuestion } from 'src/lib/flow/FlowQuestion';
import { useAppSelector } from 'src/store/hooks';
import { AnswerSet } from 'src/store/sellerFlow/AnswerSet';

type QuestionComponentProps = {
  answers: AnswerSet;
  question: FlowQuestion;
  showValidation?: boolean;
};
const QuestionComponent: React.FC<QuestionComponentProps> = ({
  answers,
  question,
  showValidation,
}) => {
  const { key } = question;
  const answer = answers[key];

  switch (question.type) {
    // basics
    case 'range':
      return (
        <NumberRangeInputField
          question={question}
          value={answer as any}
          showValidation={showValidation}
        />
      );
    case 'dropdown_number_range':
      return (
        <NumberRangeSelectInput
          question={question}
          value={answer as any}
          showValidation={showValidation}
        />
      );
    case 'multi_select':
      return (
        <CheckboxGroup
          question={question}
          values={answer as Record<string, boolean>}
          showValidation={showValidation}
        />
      );
    case 'radio_single_select':
      return (
        <RadioGroup question={question} value={answer as any} showValidation={showValidation} />
      );
    case 'combo_input':
      if (question.optionsDataSource == 'builder_partners') {
        return <BuilderPartnersSelectField question={question} value={answer as any} />;
      } else if (question.optionsDataSource == 'mls_names') {
        return <MlsNamesSelectField question={question} value={answer as any} />;
      }
      // eslint-disable-next-line no-console
      console.error(
        `Combo Input not implemented with optionsDataSource: ${question.optionsDataSource}`,
      );
      return null;
    case 'dropdown_select':
      return (
        <SelectInput question={question} value={answer as any} showValidation={showValidation} />
      );
    case 'email':
      return <EmailInput question={question} value={answer as any} />;
    case 'integer':
      return (
        <NumberInput question={question} showValidation={showValidation} value={answer as any} />
      );
    case 'phone':
      return <PhoneInput question={question} value={answer as any} />;
    case 'currency':
      return (
        <CurrencyField question={question} value={answer as any} showValidation={showValidation} />
      );
    case 'single_line':
      return (
        <SingleLineInput
          question={question}
          showValidation={showValidation}
          value={answer as any}
        />
      );
    case 'multi_line_text':
      return <TextArea question={question} showValidation={showValidation} value={answer as any} />;
    case 'image_single_select':
      return <ImageSelectInput question={question} value={answer as any} />;
    case 'element':
      return <FlowElement element={question} answer={answer} />;
    default:
      /* eslint-disable-next-line no-console */
      console.error(`Got question type: ${question.type} - but do not know how render it`);
      return null;
  }
};

interface QuestionFieldProps {
  // Required:
  answers: AnswerSet;

  //needed for recursive rendering?
  autoFocus?: boolean;
  inputLabelSize?: 'small' | 'medium' | 'large';
  question: FlowQuestion;
}

export const QuestionFieldContainer: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Flex
      className="od-question"
      // while testing reviewHomeDetails, add some styles to correctly layout
      // the home details review page it uses a gap than the default so these
      // styles are needed to hack it into the correct sizes. When we normalize
      // this we should consider if we need to add a questionLayout field to
      // page objects so that we can specify a different QuestionField component
      css={css`
        &:first-child .detail-review {
          margin-top: 0 !important;
        }
        &:last-child .detail-review {
          border-bottom: none;
        }
      `}
      flexDirection="column"
      gap="48px"
      mb={[6, 0]}
    >
      {children}
    </Flex>
  );
};

export const QuestionFieldsContainer: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Box
      id="fieldset"
      margin={0}
      padding={0}
      aria-labelledby="question-title"
      flexDirection="column"
      minWidth={325}
      gap={50}
      width="100%"
      justifyContent="flex-start"
    >
      {children}
    </Box>
  );
};

const QuestionField: React.FC<QuestionFieldProps> = ({ answers, question }) => {
  const showValidation = useAppSelector((state) => state.ui.showValidation);

  return (
    <QuestionFieldContainer>
      <QuestionComponent question={question} answers={answers} showValidation={showValidation} />
    </QuestionFieldContainer>
  );
};

export default QuestionField;
